import { Observable } from 'rxjs';
import _ from 'lodash';
import { Store } from 'redux';
import { initialize } from 'redux-form';
import { ActionsObservable } from 'redux-observable';
import { startSubmit, stopSubmit } from 'redux-form/immutable';
import { push, replace } from 'react-router-redux';

import CampaignApi from 'entities/campaign/api';
import Campaign from 'entities/campaign/model';
import { selectCampaignById } from 'entities/campaign/selectors';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import type { State } from 'reducers';
import { selectUser } from 'entities/user/selectors';
import { Features } from 'utils/features';

import { cleanDuplicateCampaign } from './utils/cleanDuplicateCampaign';
import {
  INIT,
  CAMPAIGN_PAGE_FORM,
  SAVE,
  InitAction,
  initSuccessAction,
  initFailedAction,
  saveSuccessAction,
  saveFailedAction,
  CampaignFormType,
} from './types';

const init = (action$: ActionsObservable<InitAction>, store: Store<State>) =>
  action$.ofType(INIT).mergeMap(({ id, campaignType, duplicate }: InitAction | any) => {
    const state = store.getState();
    const actions = [];
    const user = selectUser(state);
    const isActionsFeatureEnabled = user?.hasFeature(Features.ACTIONS);

    const campaign = selectCampaignById(state, id);
    const isPledge = campaign?.isPledge || campaignType === CampaignFormType.GivingPledge;

    if (id === 'new') {
      actions.push(
        Observable.of(
          initialize(
            CAMPAIGN_PAGE_FORM,
            new Campaign(
              { isPledge, allowOneTimePledge: isPledge ? true : undefined },
              isActionsFeatureEnabled
            ).toForm()
          )
        )
      );
    } else if (campaign && campaign.deeds) {
      const campaignOrDuplicatedCampaign = duplicate
        ? new Campaign(cleanDuplicateCampaign(campaign), isActionsFeatureEnabled)
        : campaign;

      if (!campaignOrDuplicatedCampaign) {
        return Observable.of(
          showErrorAction(`Campaign with id "${id}" could not be found.`),
          replace('/notFound') // Redirect to not found page
        );
      }

      actions.push(
        Observable.of(initialize(CAMPAIGN_PAGE_FORM, campaignOrDuplicatedCampaign.toForm()))
      );
    } else {
      actions.push(
        CampaignApi.fetch(id)
          .map((action) => {
            let fetchedOrDuplicatedCampaign = duplicate
              ? new Campaign(cleanDuplicateCampaign(action.campaign), isActionsFeatureEnabled)
              : action.campaign;

            if (!fetchedOrDuplicatedCampaign) {
              return Observable.of(
                showErrorAction(`Campaign with id "${id}" could not be found.`),
                replace('/notFound') // Redirect to not found page
              );
            }

            // To make Actions sorting in sections backwards compatible for the existing campaigns
            if (
              isActionsFeatureEnabled &&
              !fetchedOrDuplicatedCampaign.contentSortingSections.includes('Actions')
            ) {
              fetchedOrDuplicatedCampaign = fetchedOrDuplicatedCampaign.set(
                'contentSortingSections',
                fetchedOrDuplicatedCampaign.contentSortingSections.push('Actions')
              ) as Campaign;
            }

            return [action, initialize(CAMPAIGN_PAGE_FORM, fetchedOrDuplicatedCampaign.toForm())];
          })
          .catch((e: Error) => Observable.of(initFailedAction(e), replace('/notFound')))
      );
    }

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) => Observable.of(initFailedAction(e)));
  });

const save = (action$: ActionsObservable<any>) =>
  action$
    .ofType(SAVE)
    .mergeMap(({ data: { setCampaignActions, ...campaign } }) =>
      Observable.concat(
        Observable.of(startSubmit(CAMPAIGN_PAGE_FORM)),
        Observable.from(
          campaign.id ? CampaignApi.update(campaign.id, campaign) : CampaignApi.create(campaign)
        ).mergeMap((action) => {
          setCampaignActions(action.campaign.id);

          return [
            action,
            saveSuccessAction(action),
            stopSubmit(CAMPAIGN_PAGE_FORM),
            push('/campaigns'),
            showMessageAction(
              campaign.id ? 'Changes Saved' : `Campaign ${campaign.title} was created`
            ),
          ].filter(Boolean);
        })
      )
    )
    .catch((e) => {
      let userDisplayError = 'An error occurred during saving, please try again.';
      if (e?.responseBody?.status === 409 && e?.responseBody?.message) {
        userDisplayError = e.responseBody.message;
      }

      return Observable.of(
        saveFailedAction(e, 'saveFailed'),
        stopSubmit(CAMPAIGN_PAGE_FORM),
        showErrorAction(userDisplayError)
      );
    });

export default [init, save];
